import { gql } from '@apollo/client'

export const CONVERSATION_THREAD_ATTRIBUTES = gql`
  fragment resourceLinkConversationThreadAttributes on ConversationThread_ResourceLink {
    id
    messages {
      id
      createdAt
      content
      sender {
        id
        worker {
          id
          user {
            id
            firstName
            lastName
          }
        }
        adminUser {
          id
          user {
            id
            firstName
            lastName
          }
        }
        subcontractor {
          id
          company {
            name
          }
        }
      }
      attachments {
        id
        url
        fileName
        thumbnail {
          small
        }
      }
    }
  }
`

const NESTED_PROJECT_LOCATION_ATTRIBUTE = gql`
  fragment nestedResourceLinkProjectLocationForDefectAttributes on ProjectLocation_ResourceLink {
    id
    name
  }
`

export const PROJECT_LOCATION_ATTRIBUTES = gql`
  fragment resourceLinkProjectLocationForDefectAttributes on ProjectLocation_ResourceLink {
    ...nestedResourceLinkProjectLocationForDefectAttributes
    parent {
      ...nestedResourceLinkProjectLocationForDefectAttributes
      parent {
        ...nestedResourceLinkProjectLocationForDefectAttributes
        parent {
          ...nestedResourceLinkProjectLocationForDefectAttributes
          parent {
            ...nestedResourceLinkProjectLocationForDefectAttributes
          }
        }
      }
    }
  }
  ${NESTED_PROJECT_LOCATION_ATTRIBUTE}
`

const RESOURCE_LINK_ATTRIBUTES = gql`
  fragment resourceLinkAttributes on ResourceLink {
    id
    createdAt
    updatedAt
    slug
    active

    project {
      id
      name
    }

    template {
      id
      templateName
    }
  }
`

export const DEFECT_RESOURCE_LINK_ATTRIBUTES = gql`
  fragment defect_ResourceLinkAttributes on Defect_ResourceLink {
    id
    createdAt
    updatedAt
    archivedAt
    completedAt

    dueDate
    status
    priority

    title
    description
    type
    order
    overdue

    assigner {
      id
      user {
        id
        firstName
        lastName
      }
    }

    project {
      id
      name
      status
      country
      timezone
      locale
      builder {
        defaultLocale
        defaultTimezone
      }
    }

    assignees {
      id
      createdAt
      updatedAt
      worker {
        id
        user {
          id
          firstName
          lastName
        }
      }
      adminUser {
        id
        user {
          id
          firstName
          lastName
        }
      }
      subcontractor {
        id
        company {
          name
        }
      }
    }

    attachments {
      id
      url
      fileName
      thumbnail {
        small
      }
    }

    location {
      ...resourceLinkProjectLocationForDefectAttributes
    }

    conversation {
      ...resourceLinkConversationThreadAttributes
    }
  }
  ${PROJECT_LOCATION_ATTRIBUTES}
  ${CONVERSATION_THREAD_ATTRIBUTES}
`

const ACTION_RESOURCE_LINK_ATTRIBUTES = gql`
  fragment action_ResourceLinkAttributes on PlatformAction_ResourceLink {
    id
    createdAt

    title
    description
    status
    priorityType
    dueDate

    attachments {
      id
      url
      fileName
    }

    templateResponse {
      id
      responseData
    }

    templateResponseBlockData

    config {
      requireAttachmentToClose
    }
  }
`

const PREQUALIFICATION_RESOURCE_LINK_ATTRIBUTES = gql`
  fragment prequalification_ResourceLinkAttributes on Prequalification_ResourceLink {
    id
    status

    builder {
      defaultLocale
      defaultTimezone
    }

    subcontractor {
      id
      company {
        name
      }
    }

    templateResponse {
      id
      responseData
      template {
        templateData
        id
      }
    }
  }
`

const NESTED_DOCUMENT_FOLDER_RESOURCE_LINK_ATTRIBUTES = gql`
  fragment nestedDocumentFolderResourceLinkAttributes on DocumentFolder_ResourceLink {
    id
    name
    childrenFolders {
      id
      name
    }
    drawings {
      id
      drawingNumber
      drawingRevision
      url
    }
  }
`

const DOCUMENT_FOLDER_RESOURCE_LINK_ATTRIBUTES = gql`
  fragment documentFolder_ResourceLinkAttributes on DocumentFolder_ResourceLink {
    ...nestedDocumentFolderResourceLinkAttributes
    childrenFolders {
      ...nestedDocumentFolderResourceLinkAttributes
      childrenFolders {
        ...nestedDocumentFolderResourceLinkAttributes
        childrenFolders {
          ...nestedDocumentFolderResourceLinkAttributes
          childrenFolders {
            ...nestedDocumentFolderResourceLinkAttributes
            childrenFolders {
              ...nestedDocumentFolderResourceLinkAttributes
              childrenFolders {
                ...nestedDocumentFolderResourceLinkAttributes
                childrenFolders {
                  ...nestedDocumentFolderResourceLinkAttributes
                  childrenFolders {
                    ...nestedDocumentFolderResourceLinkAttributes
                    childrenFolders {
                      ...nestedDocumentFolderResourceLinkAttributes
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${NESTED_DOCUMENT_FOLDER_RESOURCE_LINK_ATTRIBUTES}
`

const RESOURCE_LINK_ATTRIBUTES_ENTITY = gql`
  fragment resourceLinkAttributesEntity on ResourceLinkEntity {
    id
    createdAt
    updatedAt
    slug
    active

    builder {
      id
      logoUrl
      company {
        name
      }
      hideBuildPassLogos
      defaultLocale
      defaultTimezone
    }

    project {
      id
      createdAt
      name
      address
      timezone
      projectCode
      country
      locale
      builder {
        defaultLocale
        defaultTimezone
      }
    }

    templateId
    template {
      id
      templateType
      templateName
      templateData
      builder {
        defaultLocale
        defaultTimezone
      }
    }

    registerItemId
    registerItem {
      id
      name
      identifier
      fullIdentifier

      checklistTemplates {
        id

        templateType
        templateName
        templateData
      }

      checklistResourceLinks {
        id

        active
        slug

        project {
          id
        }

        template {
          id
        }
      }
    }

    prequalification {
      ...prequalification_ResourceLinkAttributes
    }

    documentFolder {
      ...documentFolder_ResourceLinkAttributes
    }

    contact {
      id
      name
      subcontractor {
        id
        company {
          name
        }
        defects {
          ...defect_ResourceLinkAttributes
        }
      }
    }
  }
  ${DEFECT_RESOURCE_LINK_ATTRIBUTES}
  ${PREQUALIFICATION_RESOURCE_LINK_ATTRIBUTES}
  ${DOCUMENT_FOLDER_RESOURCE_LINK_ATTRIBUTES}
`

export const GET_RESOURCE_LINK = gql`
  query resourceLinkBySlug_UI($slug: String!) {
    resourceLinkBySlug(slug: $slug) {
      ... on ResourceLinkEntity {
        ...resourceLinkAttributesEntity
      }
      ... on DeactivatedResourceLinkEntity {
        id
        active
      }
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES_ENTITY}
`

export const GET_RESOURCE_LINK_BY_RESOURCE = gql`
  query resourceLinkByResource_UI($filter: ResourceLinkByResourceFilterInput!) {
    resourceLinkByResource(filter: $filter) {
      ...resourceLinkAttributes
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES}
`

export const FIND_OR_CREATE_RESOURCE_LINK = gql`
  mutation findOrCreateResourceLink_UI($input: CreateResourceLinkInput!) {
    findOrCreateResourceLink(input: $input) {
      ...resourceLinkAttributes
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES}
`

// BULK
export const FIND_OR_CREATE_RESOURCE_LINKS = gql`
  mutation findOrCreateResourceLinks_UI($input: [CreateResourceLinkInput!]!) {
    findOrCreateResourceLinks(input: $input) {
      ...resourceLinkAttributes
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES}
`

export const CREATE_RESOURCE_LINK = gql`
  mutation createResourceLink_UI($input: CreateResourceLinkInput!) {
    createResourceLink(input: $input) {
      ...resourceLinkAttributes
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES}
`

export const UPDATE_RESOURCE_LINK = gql`
  mutation updateResourceLink_UI(
    $id: String!
    $input: UpdateResourceLinkInput!
  ) {
    updateResourceLink(id: $id, input: $input) {
      ...resourceLinkAttributes
    }
  }
  ${RESOURCE_LINK_ATTRIBUTES}
`

export const GET_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK = gql`
  query publicPlatformActionViaChecklistResourceLink_UI(
    $id: String!
    $resourceLinkId: String!
  ) {
    publicPlatformActionViaChecklistResourceLink(
      id: $id
      resourceLinkId: $resourceLinkId
    ) {
      ...action_ResourceLinkAttributes
    }
  }
  ${ACTION_RESOURCE_LINK_ATTRIBUTES}
`

export const CREATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK = gql`
  mutation createPublicPlatformActionViaChecklistResourceLink_UI(
    $input: CreatePublicPlatformActionViaChecklistResourceLinkInput!
  ) {
    createPublicPlatformActionViaChecklistResourceLink(input: $input) {
      ...action_ResourceLinkAttributes
    }
  }
  ${ACTION_RESOURCE_LINK_ATTRIBUTES}
`

export const UPDATE_PUBLIC_ACTION_VIA_CHECKLIST_RESOURCE_LINK = gql`
  mutation updatePublicPlatformActionViaChecklistResourceLink_UI(
    $id: String!
    $input: UpdatePublicPlatformActionViaChecklistResourceLinkInput!
  ) {
    updatePublicPlatformActionViaChecklistResourceLink(id: $id, input: $input) {
      ...action_ResourceLinkAttributes
    }
  }
  ${ACTION_RESOURCE_LINK_ATTRIBUTES}
`

export const UPDATE_PREQUALIFICATION_VIA_RESOURCE_LINK = gql`
  mutation updatePrequalificationViaResourceLink_UI(
    $resourceLinkSlug: String!
    $prequalificationId: String!
    $subcontractorId: String!
    $input: UpdateResourceLinkPrequalificationInput!
  ) {
    updatePrequalificationViaResourceLink(
      resourceLinkSlug: $resourceLinkSlug
      prequalificationId: $prequalificationId
      subcontractorId: $subcontractorId
      input: $input
    ) {
      ...prequalification_ResourceLinkAttributes
    }
  }
  ${PREQUALIFICATION_RESOURCE_LINK_ATTRIBUTES}
`
